import {
    POItemShipmentGroup,
    PurchaseOrder,
    PurchaseOrderItem,
    PurchaseOrderItemStatus,
    PurchaseOrderRecommendation,
} from "@domain/purchasing";

import { usePurchaseOrderItems, usePurchasing } from "@features/purchasing/hooks";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Stack, Table, TableBody, TableContainer, Tooltip, Typography, useTheme } from "@mui/material";
import { useBottomSheet } from "@shared/shell/bottomsheet/useBottomSheet";
import { useOrderPlacedDialogStore } from "@features/purchasing/orderPlacedDialogStore";
import { useEffect, useMemo, useState } from "react";
import { PurchaseOrderGroup } from "./PurchaseOrderGroup";
import { PurchaseOrderItemDetails } from "../items/PurchaseOrderItemDetails";
import { PurchaseOrderNotes } from "../notes/PurchaseOrderNotes";
import { JSEditableInput, Label } from "@shared/ui/common";
import DialogHeader from "@shared/ui/dialog/DialogHeader";
import { Close } from "@mui/icons-material";
import { BottomSheet } from "@shared/shell/bottomsheet/BottomSheet";
import { useShipments } from "@features/shipments";
import { PurchaseOrderItemRow } from "./PurchaseOrderItemRow";
import { Shipment } from "@domain/shipment";
import { JSDatePicker } from "@shared/ui/common/JSDatePicker";
import { addDays, differenceInDays, startOfDay } from "date-fns";
import { groupByShipment } from "@features/purchasing/utils/groupItems";
import { PurchaseOrderShipment } from "../shipment/PurchaseOrderShipment";
import { Icon } from "@shared/icons";
import { Product } from "@domain/product/models/Product";
import { BorderedBlock } from "@shared/ui/common/BorderedBlock";
import { PurchaseOrderProducts } from "../items/PurchaseOrderProducts";
import { useSuppliers } from "@features/suppliers/hooks/useSuppliers";
import { IconSize } from "@shared/icons/Icon";
import { useToast } from "@shared/hooks/useToast";
import { DeleteDialog } from "@shared/ui/dialog/DeleteDialog";
import { useSupplier } from "@features/suppliers";

function purchaseOrderRecommendationMatchesItem(
    item: PurchaseOrderItem,
    rec: PurchaseOrderRecommendation,
): boolean {
    if (item.id != null && rec.item.id != null) {
        return item.id === rec.item.id;
    }
    return rec.item.product.id === item.product.id;
}

interface PurchaseOrderDraftProps {
    purchaseOrderId: string;
}

export const PurchaseOrderDraft: React.FC<PurchaseOrderDraftProps> = ({ purchaseOrderId }) => {
    const theme = useTheme();

    const { success } = useToast();

    // Navigation states
    const { closeBottomSheet } = useBottomSheet();
    const [detailBottomSheet, setDetailBottomSheet] = useState<React.ReactNode>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    // Purchase order states
    const { purchaseOrder, createPurchaseOrderItem, updatePurchaseOrder, publishPurchaseOrder, deletePurchaseOrder, isDeleting, calculateAmounts } = usePurchasing(purchaseOrderId);
    const { supplier } = useSupplier(purchaseOrder?.supplierId ?? '');


    const { updatePurchaseOrderItem, deletePurchaseOrderItem } = usePurchaseOrderItems();
    const [shipmentGroups, setShipmentGroups] = useState<POItemShipmentGroup[]>([]);
    const [isAddingItems, setIsAddingItems] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSending, setIsSending] = useState(false);

    // Purchase order items states
    const [purchaseOrderItems, setPurchaseOrderItems] = useState<PurchaseOrderItem[]>([]);

    const { updateShipment } = useShipments();

    const [isProcessingItems, setIsProcessingItems] = useState(false);
    const [recommendations, setRecommendations] = useState<PurchaseOrderRecommendation[]>([]);
    /** Line totals (qty × price) toggled via icon next to total amount. */
    const [showOrderRowAmounts, setShowOrderRowAmounts] = useState(false);


    // Process purchase order items with ETA and recommendations
    const processPurchaseOrderItems = async (purchaseOrder: PurchaseOrder): Promise<PurchaseOrderItem[]> => {
        if (!purchaseOrder.id) {
            setRecommendations([]);
            return purchaseOrder.items;
        }

        const recs = await calculateAmounts(purchaseOrder.id);
        setRecommendations(recs);

        return purchaseOrder.items;
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        success('Copied to clipboard');
    }

    // Group the items by shipment and calculate ETAs/recommendations
    useEffect(() => {
        if (!purchaseOrder?.items) {
            setPurchaseOrderItems([]);
            setShipmentGroups([]);
            return;
        }

        const processItems = async () => {
            setIsProcessingItems(true);
            try {
                // Process items with ETA and recommendations
                const processedItems = await processPurchaseOrderItems(purchaseOrder);
                setPurchaseOrderItems(processedItems);

                // Group items by shipment
                const uniqueShipments = groupByShipment(processedItems);
                setShipmentGroups(uniqueShipments);

            } catch (error) {
                console.error('Error processing purchase order items:', error);
            } finally {
                setIsProcessingItems(false);
            }
        };

        processItems();
    }, [purchaseOrder]);

    const handleShowAddProduct = () => {
        setDetailBottomSheet(
            <PurchaseOrderProducts
                handleClose={handleCloseDetailBottomSheet}
                handleNext={addItems}
                addedProducts={purchaseOrderItems?.map(item => item.product) || []}
                supplierId={purchaseOrder?.supplierId ?? purchaseOrder?.supplier?.id}
            />
        )
    }

    const addItems = async (products: Product[], _supplierId?: string) => {
        if (!purchaseOrder?.id) return;

        handleCloseDetailBottomSheet();
        setIsAddingItems(true);
        setIsSaving(true);

        try {
            const poSupplierId = purchaseOrder?.supplierId ?? purchaseOrder?.supplier?.id;
            for (const product of products) {
                const link = poSupplierId
                    ? supplier?.supplierProducts.find((p) => p.product?.id === product.id)
                    : undefined;
                await createPurchaseOrderItem({
                    purchaseOrderId: purchaseOrder.id, item: {
                        product,
                        supplierId: poSupplierId ?? undefined,
                        minimumOrderQuantity: link?.minimumOrderQuantity,
                        orderInMultiplesOf: link?.orderInMultiplesOf,
                        minimumOrderQuantityType: link?.minimumOrderQuantityType,
                        productionStartDate: new Date(),
                        productionReadyDate: addDays(new Date(), product.productionDuration ?? 0),
                        status: PurchaseOrderItemStatus.PLANNED,
                        shipment: undefined,
                        eta: addDays(purchaseOrder?.orderDate || new Date(),
                            (product.productionDuration ?? 0) +
                            (product.defaultShipmentMethod?.duration ?? 0) +
                            (product.processingDuration ?? 0)
                        ),
                    }
                });
            }
        } finally {
            setIsAddingItems(false);
            setIsSaving(false);
        }
    }

    const handleCloseDetailBottomSheet = () => {
        setDetailBottomSheet(null);
    }

    const sendPurchaseOrder = async () => {
        setIsSending(true);
        try {
            for (const shipment of shipmentGroups || []) {
                if (shipment.shipment && shipment.shipment.id) {
                    await updateShipment({ id: shipment.shipment.id, request: { draft: false } });
                }
            }

            await publishPurchaseOrder(purchaseOrderId);

            const payload = {
                purchaseOrderId,
                poNumber: purchaseOrder?.poNumber,
                supplierEmail: supplier?.email,
                supplierName: supplier?.name,
                supplierPhone: supplier?.phone,
                supplierAddressLine: supplier?.addresses.find(a => a.isPrimary)?.address,
            };

            await closeBottomSheet();
            useOrderPlacedDialogStore.getState().open(payload);
        } catch (error) {
            console.error('Failed to place purchase order:', error);
        } finally {
            setIsSending(false);
        }
    }

    const handleDeletePurchaseOrder = () => {
        setShowDeleteDialog(true);
    }

    const confirmDeletePurchaseOrder = async () => {
        try {
            await deletePurchaseOrder(purchaseOrderId);
            setShowDeleteDialog(false);
            closeBottomSheet();
        } catch (error) {
            console.error('Failed to delete purchase order:', error);
            // You might want to show a toast notification here
        }
    }

    const cancelDeletePurchaseOrder = () => {
        setShowDeleteDialog(false);
    }

    // Calculate new shipment dates for draft shipments
    const calculateNewShipmentDates = (newOrderDate: Date, shipment: Shipment) => {
        const oldOrderDate = purchaseOrder?.orderDate;
        if (!oldOrderDate || !shipment.draft) return shipment;

        // Calculate the difference in days between old and new order date
        const daysDifference = differenceInDays(startOfDay(newOrderDate), startOfDay(oldOrderDate));

        return {
            ...shipment,
            departureDate: addDays(shipment.departureDate, daysDifference),
            arrivalDate: addDays(shipment.arrivalDate, daysDifference),
            processedDate: addDays(shipment.processedDate, daysDifference),
        };
    };

    // Handle order date change and update all related dates
    const handleOrderDateChange = async (newOrderDate: Date) => {
        setIsSaving(true);
        if (!purchaseOrder || !purchaseOrderItems) return;

        try {
            // Update the purchase order with new order date
            await updatePurchaseOrder({ id: purchaseOrderId, purchaseOrder: { orderDate: newOrderDate } });

            // Update all purchase order items with new production dates
            for (const item of purchaseOrderItems) {
                if (item.id) {
                    const newProductionStartDate = newOrderDate;
                    const newProductionReadyDate = addDays(newOrderDate, item.product.productionDuration ?? 0);

                    await updatePurchaseOrderItem({
                        itemId: item.id,
                        eta: addDays(newOrderDate,
                            (item.product.productionDuration ?? 0) +
                            (item.product.defaultShipmentMethod?.duration ?? 0) +
                            (item.product.processingDuration ?? 0)),
                        productionStartDate: newProductionStartDate,
                        productionReadyDate: newProductionReadyDate,
                        orderId: purchaseOrderId,
                    });
                }
            }

            // Update draft shipments with new dates
            const draftShipments = shipmentGroups
                .filter(group => group.shipment?.draft)
                .map(group => group.shipment!);

            for (const shipment of draftShipments) {
                if (shipment.id) {
                    const updatedShipment = calculateNewShipmentDates(newOrderDate, shipment);
                    await updateShipment({
                        id: shipment.id,
                        request: {
                            departureDate: updatedShipment.departureDate,
                            arrivalDate: updatedShipment.arrivalDate,
                            processedDate: updatedShipment.processedDate,
                        },
                        purchaseOrderId: purchaseOrderId,
                    });
                }
            }

        } catch (error) {
            console.error('Failed to update dates:', error);
            // You might want to show a toast notification here
        } finally {
            setIsSaving(false);
        }
    };

    const handleOpenItemDetails = (item: PurchaseOrderItem) => {
        setDetailBottomSheet(<PurchaseOrderItemDetails
            item={item}
            shipment={item.shipment}
            orderDate={purchaseOrder?.orderDate || new Date()}
            onClose={handleCloseDetailBottomSheet}
            onRemove={(item) => deletePurchaseOrderItem({ purchaseOrderId, itemId: item.id! })}
            recommendation={recommendations.find(r => purchaseOrderRecommendationMatchesItem(item, r))}
            onShipmentChange={(item, shipment) => updatePurchaseOrderItem({ itemId: item.id!, shipmentId: shipment.id! })}
            availableShipments={shipmentGroups.map(group => group.shipment!).filter(shipment => shipment.id)}
            onItemUpdate={(item) => updatePurchaseOrderItem({ itemId: item.id!, ...item, orderId: purchaseOrderId })}
        />)
    }

    const handleQuantityChange = async (quantity: number | undefined, item: PurchaseOrderItem) => {
        setIsSaving(true);
        try {
            const nextQuantity = quantity || 0;
            await updatePurchaseOrderItem({ itemId: item.id!, quantity: nextQuantity });

            const applyQuantity = (line: PurchaseOrderItem) =>
                line.id === item.id ? { ...line, quantity: nextQuantity } : line;

            setPurchaseOrderItems((items) => items.map(applyQuantity));
            setShipmentGroups((groups) =>
                groups.map((group) => ({
                    ...group,
                    items: group.items.map(applyQuantity),
                })),
            );
        } finally {
            setIsSaving(false);
        }
    }

    const handleShowAddShipment = (items: PurchaseOrderItem[], shipment?: Shipment) => {
        if (!purchaseOrder) return;

        setDetailBottomSheet(
            <PurchaseOrderShipment
                handleFinish={handleCloseDetailBottomSheet}
                handleClose={handleCloseDetailBottomSheet}
                products={items?.map(item => item.product) || []}
                purchaseOrder={purchaseOrder}
                purchaseOrderItems={items || []}
                selectedShipment={shipment}
            />)
    }

    const formatPrice = (price: number, currency?: string) => {
        if (currency) {
            return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(price);
        }
        return price.toLocaleString();
    }

    const getTotalAmount = () => {
        return purchaseOrderItems?.reduce((acc, item) => acc + ((item.purchasePrice || 0) * (item.quantity || 0)), 0);
    }

    return (
        <>
            <DialogHeader>
                <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                    <Stack direction="row" gap={2} alignItems="center" justifyContent="flex-start">
                        <Typography variant="h6" component="div">
                            Your purchase order
                        </Typography>

                        {purchaseOrder?.draft && (
                            <Label
                                currentValue="draft"
                                options={[
                                    { label: 'Draft', color: theme.palette.info.main, value: 'draft' },
                                ]}
                            />
                        )}
                    </Stack>

                    <Stack direction="row" gap={2} alignItems="center" justifyContent="flex-end">

                        {isSaving ? (
                            <Stack direction="row" gap={1} alignItems="center" justifyContent="flex-end">
                                <Typography variant="body2" color="text.secondary">Saving {purchaseOrder?.draft ? 'draft' : 'order'}</Typography>
                                <CircularProgress size={16} />
                            </Stack>
                        ) : (
                            <Stack direction="row" gap={1} alignItems="center" justifyContent="flex-end">
                                <Typography variant="body2" color="text.secondary"> {purchaseOrder?.draft ? 'Draft saved' : 'Order saved'}</Typography>
                                <Icon icon="CheckContained" color={theme.palette.text.secondary} />
                            </Stack>
                        )}

                        < IconButton onClick={closeBottomSheet}>
                            <Close />
                        </IconButton>
                    </Stack>
                </Stack>
            </DialogHeader >

            <DialogContent sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                p: 4,
            }}>
                <Stack direction="column" gap={4}>

                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography variant="h6" color="text.primary" noWrap >
                            {supplier?.name ?? "No supplier"}
                        </Typography>

                        <Stack direction="row" alignItems="center" gap={0.5}>

                            {supplier?.email && (
                                <Tooltip title={`${supplier.email}`}>
                                    <IconButton onClick={() => copyToClipboard(supplier.email!)}>
                                        <Icon icon="Mail01" color={theme.palette.text.primary} size={IconSize.LARGE} />
                                    </IconButton>
                                </Tooltip>
                            )}

                            {supplier?.phone && (
                                <Tooltip title={`${supplier.phone}`}>
                                    <IconButton onClick={() => copyToClipboard(supplier.phone!)}>
                                        <Icon icon="Phone" color={theme.palette.text.primary} size={IconSize.LARGE} />
                                    </IconButton>
                                </Tooltip>
                            )}

                            {supplier?.website && (
                                <Tooltip title={supplier.website}>
                                    <IconButton onClick={() => copyToClipboard(supplier.website!)}>
                                        <Icon icon="Globe05" color={theme.palette.text.primary} size={IconSize.LARGE} />
                                    </IconButton>
                                </Tooltip>
                            )}

                        </Stack>
                    </Stack>

                    <Divider />


                    {/* Order Details */}
                    <Stack spacing={2} direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" sx={{ gap: 2 }}>


                        <Stack direction="column" gap={1} flex={1} >

                            <Typography variant="subtitle2" component="div">Order Date</Typography>

                            <JSDatePicker
                                sx={{
                                    flex: 1,
                                }}
                                value={purchaseOrder?.orderDate}
                                onChange={(date) => handleOrderDateChange(date as Date)}
                            />
                        </Stack>


                        <Stack direction="column" gap={1} flex={1} >
                            <Typography variant="subtitle2" component="div">PO Number</Typography>
                            <JSEditableInput
                                outlined
                                placeholder="PO Number"
                                type="text"
                                value={purchaseOrder?.poNumber || undefined}
                                onValueChange={(value) => updatePurchaseOrder({ id: purchaseOrderId, purchaseOrder: { poNumber: value || undefined } })}
                            />
                        </Stack>

                    </Stack>


                </Stack>

                {/* Main Content */}
                <Stack gap={2}>
                    <Typography variant="subtitle2" component="div">
                        Order items
                    </Typography>

                    <Stack flex={1} gap={4}>
                        {shipmentGroups?.map((group, index) => (
                            <>
                                <PurchaseOrderGroup
                                    key={index}
                                    group={group}
                                    onChangeShipment={(group) => handleShowAddShipment(group.items, group.shipment)}
                                >

                                    <TableContainer>
                                        <Table  >
                                            <TableBody>
                                                {group.items.map((item) => {
                                                    return (
                                                        <PurchaseOrderItemRow
                                                            key={`${item.id}-${index}`}
                                                            item={item}
                                                            shipment={group.shipment}
                                                            recommendation={recommendations.find(r => purchaseOrderRecommendationMatchesItem(item, r))}
                                                            onQuantityChange={handleQuantityChange}
                                                            onOpenItemDetails={handleOpenItemDetails}
                                                            showLineAmount={showOrderRowAmounts}
                                                        />
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </PurchaseOrderGroup>
                            </>
                        ))}


                        {purchaseOrderItems?.length > 0 ? (
                            <TableContainer>
                                <Table>
                                    <TableBody>
                                        {purchaseOrderItems?.filter(item => !item.shipment)?.sort((a, b) => (a.createdAt || new Date()).getTime() - (b.createdAt || new Date()).getTime()).map((item) => (
                                            <PurchaseOrderItemRow
                                                key={item.id}
                                                item={item}
                                                shipment={item.shipment}
                                                recommendation={recommendations.find(r => purchaseOrderRecommendationMatchesItem(item, r))}
                                                onQuantityChange={handleQuantityChange}
                                                onOpenItemDetails={handleOpenItemDetails}
                                                showLineAmount={showOrderRowAmounts}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                        ) : (
                            <BorderedBlock>
                                {isProcessingItems && (
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        py: 2,
                                        gap: 2
                                    }}>
                                        <CircularProgress size={20} />
                                        <Typography variant="body2" color="text.secondary">
                                            Calculating recommendations...
                                        </Typography>
                                    </Box>
                                )}


                                {!isAddingItems && !isProcessingItems && (
                                    <>
                                        <Typography variant="subtitle2" color="text.primary">Your purchase order is empty</Typography>
                                        <Typography variant="body2" color="text.secondary">Start by adding products to your purchase order</Typography>
                                    </>
                                )}
                            </BorderedBlock>
                        )}

                        {/* Loading indicator when adding items */}
                        {isAddingItems && (
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                py: 2,
                                gap: 2
                            }}>
                                <CircularProgress size={20} />
                                <Typography variant="body2" color="text.secondary">
                                    Adding products...
                                </Typography>
                            </Box>
                        )}

                    </Stack>

                    <Stack direction="row" gap={2} alignItems="center" justifyContent="flex-start">
                        <Stack direction="row" gap={2} alignItems="center" justifyContent="flex-start" flex={1}>
                            <Button variant="text" color="secondary" startIcon={<Icon icon="Plus01" />} onClick={handleShowAddProduct}>
                                Add Products
                            </Button>

                            <Button variant="text" color="secondary" onClick={() => handleShowAddShipment(purchaseOrderItems)}>
                                Add Shipment
                            </Button>
                        </Stack>

                        {/* {supplier && (
                            <Stack direction="row" gap={1} alignItems="center" justifyContent="flex-start">
                                <Typography variant="subtitle2" color="text.primary">
                                    Total amount
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {formatPrice(getTotalAmount() ?? 0)}
                                </Typography>
                                <Tooltip title={showOrderRowAmounts ? 'Hide line amounts' : 'Show line amounts'}>
                                    <IconButton
                                        size="small"
                                        aria-pressed={showOrderRowAmounts}
                                        aria-label={showOrderRowAmounts ? 'Hide line amounts' : 'Show line amounts'}
                                        onClick={() => setShowOrderRowAmounts((v) => !v)}
                                        edge="end"
                                    >
                                        {showOrderRowAmounts ? (
                                            <Icon
                                                icon="EyeOpen"
                                                color={theme.palette.text.secondary}
                                                size={IconSize.SMALL}
                                            />
                                        ) : (
                                            <Icon
                                                icon="EyeClosed"
                                                color={theme.palette.text.secondary}
                                                size={IconSize.SMALL}
                                            />
                                        )}
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        )} */}

                    </Stack>
                </Stack>

                {/* Main Content Actions */}
                {purchaseOrder?.draft && (
                    <Stack direction="row" alignItems="center" gap={2} sx={{
                        border: 1,
                        borderColor: theme.palette.primary.main,
                        p: 2,
                        borderRadius: 2,
                        justifyContent: 'space-between',
                        backgroundColor: theme.palette.background.default,
                    }}>
                        <Typography color="primary" variant="subtitle2" component="div">
                            {!purchaseOrder.items?.length ? 'Add products to your purchase order first' : 'Is your purchase order ready to be sent?'}
                        </Typography>

                        <Button endIcon={isSending ? <CircularProgress color="inherit" size={20} /> : <Icon icon="Send01" size={IconSize.SMALL} />} disabled={!purchaseOrder.items?.length} variant="contained" color="primary" onClick={() => sendPurchaseOrder()}>
                            Place order
                        </Button>
                    </Stack>
                )}

                {/* Notes */}
                <Stack gap={2}>
                    <Typography variant="subtitle2" component="div">
                        Notes
                    </Typography>

                    {purchaseOrder && (

                        <PurchaseOrderNotes
                            purchaseOrder={purchaseOrder}
                        />
                    )}
                </Stack>

                {/* Delete Purchase Order */}
                <Stack direction="row" gap={2} alignItems="center" justifyContent="flex-end">
                    <Button
                        variant="text"
                        color="error"
                        onClick={handleDeletePurchaseOrder}
                    >
                        Delete
                    </Button>
                </Stack>

            </DialogContent>

            <BottomSheet
                open={Boolean(detailBottomSheet)}
                onClose={() => setDetailBottomSheet(null)}
            >
                {detailBottomSheet}
            </BottomSheet>


            <DeleteDialog
                open={showDeleteDialog}
                title="Delete Purchase Order"
                description="Are you sure you want to delete this purchase order? This action cannot be undone."
                cancelDelete={cancelDeletePurchaseOrder}
                confirmDelete={confirmDeletePurchaseOrder}
                isDeleting={isDeleting}
            />
        </>
    );
};